import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Circle,
  CheckCircle2,
  AlertCircle,
  Radio,
} from 'lucide-react';
import { usersService, AdminStats, getErrorMessage } from '@/services';
import toast from 'react-hot-toast';

function formatRelativeTime(dateStr: string | null, t: (key: string) => string): string {
  if (!dateStr) return t('admin.neverConnected');
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin}m fa`;
  if (diffH < 24) return `${diffH}h fa`;
  if (diffD === 1) return 'ieri';
  if (diffD < 7) return `${diffD}gg fa`;
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getUserStatus(
  user: AdminStats['userList'][0],
  t: (key: string) => string
): { label: string; className: string } {
  if (!user.is_active) {
    return { label: t('common.disabled'), className: 'bg-gray-100 text-gray-600' };
  }
  if (user.expires_at) {
    const expDate = new Date(user.expires_at);
    const now = new Date();
    if (expDate < now) {
      return { label: t('admin.expired'), className: 'bg-red-50 text-red-600' };
    }
    const diffDays = Math.ceil((expDate.getTime() - now.getTime()) / 86400000);
    if (diffDays <= 7) {
      return { label: t('admin.expiringSoon'), className: 'bg-amber-50 text-amber-600' };
    }
  }
  return { label: t('common.active'), className: 'bg-emerald-50 text-emerald-600' };
}

function formatExpiry(expiresAt: string | null, t: (key: string) => string): string {
  if (!expiresAt) return t('admin.noExpiry');
  return new Date(expiresAt).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-dark-300">{label}</span>
        <span className="text-dark-400 font-medium">
          {value}/{max}
        </span>
      </div>
      <div className="h-2 bg-dark-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary-500 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await usersService.getAdminStats();
        setStats(data);
      } catch (error) {
        toast.error(getErrorMessage(error) || t('common.error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [t]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('admin.dashboard')}</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card">
              <div className="animate-pulse">
                <div className="h-4 bg-dark-700 rounded w-24 mb-2" />
                <div className="h-8 bg-dark-700 rounded w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: t('admin.totalUsers'),
      value: stats?.totalUsers || 0,
      icon: <Users size={20} className="text-primary-600" />,
      bg: 'bg-primary-50',
    },
    {
      label: t('admin.activeUsers'),
      value: stats?.activeUsers || 0,
      icon: <UserCheck size={20} className="text-emerald-600" />,
      bg: 'bg-emerald-50',
    },
    {
      label: t('admin.expiredUsers'),
      value: stats?.expiredUsers || 0,
      icon: <UserX size={20} className="text-red-600" />,
      bg: 'bg-red-50',
    },
    {
      label: t('admin.expiringSoon'),
      value: stats?.expiringSoon || 0,
      icon: <Clock size={20} className="text-amber-600" />,
      bg: 'bg-amber-50',
    },
  ];

  const totalUsers = stats?.totalUsers || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('admin.dashboard')}</h1>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-400 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold text-dark-100 mt-1">{stat.value}</p>
              </div>
              <div className={`w-11 h-11 rounded-lg ${stat.bg} flex items-center justify-center`}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two columns: User List + System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User List — 2/3 width */}
        <div className="lg:col-span-2 card">
          <h2 className="text-base font-semibold text-dark-100 mb-4">
            {t('admin.userOverview')}
          </h2>
          {stats?.userList && stats.userList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-700">
                    <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('auth.username')}</th>
                    <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('common.status')}</th>
                    <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('admin.expiresAt')}</th>
                    <th className="text-left py-2 pr-4 text-dark-400 font-medium">{t('admin.lastAccess')}</th>
                    <th className="text-center py-2 text-dark-400 font-medium">Live</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.userList.map((user) => {
                    const status = getUserStatus(user, t);
                    return (
                      <tr
                        key={user.id}
                        className="border-b border-dark-700 last:border-0 hover:bg-dark-800/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        <td className="py-2.5 pr-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-md bg-primary-50 flex items-center justify-center text-xs font-bold text-primary-600 flex-shrink-0">
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-dark-100">{user.username}</span>
                          </div>
                        </td>
                        <td className="py-2.5 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.className}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-dark-300">
                          {formatExpiry(user.expires_at, t)}
                        </td>
                        <td className="py-2.5 pr-4 text-dark-400">
                          {formatRelativeTime(user.last_login_at, t)}
                        </td>
                        <td className="py-2.5 text-center">
                          {user.live_events > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 text-xs font-medium">
                              <Radio size={10} className="animate-pulse" />
                              {user.live_events}
                            </span>
                          ) : (
                            <span className="text-dark-500">0</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-dark-400 text-sm">{t('common.noData')}</p>
          )}
        </div>

        {/* System Status — 1/3 width */}
        <div className="space-y-6">
          {/* System Config */}
          <div className="card">
            <h2 className="text-base font-semibold text-dark-100 mb-4">
              {t('admin.systemStatus')}
            </h2>
            <div className="space-y-3">
              <ProgressBar
                value={stats?.systemStats.daznConfigured || 0}
                max={totalUsers}
                label={t('admin.daznConfigured')}
              />
              <ProgressBar
                value={stats?.systemStats.proxyConfigured || 0}
                max={totalUsers}
                label={t('admin.proxyConfigured')}
              />
              <ProgressBar
                value={stats?.systemStats.xaccelConfigured || 0}
                max={totalUsers}
                label={t('admin.xaccelConfigured')}
              />
              <ProgressBar
                value={stats?.systemStats.setupCompleted || 0}
                max={totalUsers}
                label={t('admin.setupCompletedLabel')}
              />
            </div>
          </div>

          {/* Events Today */}
          <div className="card">
            <h2 className="text-base font-semibold text-dark-100 mb-4">
              {t('admin.eventsToday')}
            </h2>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Radio size={14} className="text-emerald-500" />
                  <span className="text-sm text-dark-300">{t('admin.liveCount')}</span>
                </div>
                <span className="text-sm font-semibold text-dark-100">
                  {stats?.eventStats.live || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Circle size={14} className="text-blue-500" />
                  <span className="text-sm text-dark-300">{t('admin.scheduledCount')}</span>
                </div>
                <span className="text-sm font-semibold text-dark-100">
                  {stats?.eventStats.scheduled || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-gray-400" />
                  <span className="text-sm text-dark-300">{t('admin.completedCount')}</span>
                </div>
                <span className="text-sm font-semibold text-dark-100">
                  {stats?.eventStats.completed || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} className="text-red-500" />
                  <span className="text-sm text-dark-300">{t('admin.errorCount')}</span>
                </div>
                <span className="text-sm font-semibold text-dark-100">
                  {stats?.eventStats.errors || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity (Last Logins) */}
      <div className="card">
        <h2 className="text-base font-semibold text-dark-100 mb-4">
          {t('admin.lastAccess')}
        </h2>
        {stats?.userList && stats.userList.length > 0 ? (
          <div className="space-y-2">
            {stats.userList.map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-3 py-2 border-b border-dark-700 last:border-0"
              >
                <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center text-sm font-bold text-primary-600 flex-shrink-0">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-dark-100">{user.username}</span>
                </div>
                <span className="text-sm text-dark-400">
                  {user.last_login_at
                    ? new Date(user.last_login_at).toLocaleString('it-IT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : t('admin.neverConnected')}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-dark-400 text-sm">{t('common.noData')}</p>
        )}
      </div>
    </div>
  );
}
